import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const KEY = "fx_cookie_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [preferences, setPreferences] = useState({ functional: true, analytics: false });
  const [expand, setExpand] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (!c) setShow(true);
    else setPreferences({ functional: !!c.functional, analytics: !!c.analytics });
  }, []);

  const save = (choice) => {
    localStorage.setItem(KEY, JSON.stringify({ ...choice, decided_at: new Date().toISOString() }));
    setShow(false);
  };

  if (!show) return null;
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50 fx-panel p-4 shadow-2xl border border-cyan-500/30" data-testid="cookie-banner">
      <div className="flex items-start gap-3">
        <Cookie size={18} className="text-cyan-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-100 font-medium">We use cookies</div>
          <div className="text-xs text-slate-400 mt-1 leading-relaxed">
            Strictly necessary cookies are always on so the app can sign you in and work. Functional cookies remember your preferences. Choose what else we may set.
          </div>
          {expand && (
            <div className="mt-3 space-y-2 border-t border-[#1E293B] pt-3" data-testid="cookie-preferences">
              <div className="flex items-center justify-between text-xs">
                <div><div className="text-slate-200">Strictly necessary</div><div className="text-slate-500">Auth, security, load balancing</div></div>
                <span className="text-slate-500 text-[10px] uppercase tracking-widest">Always on</span>
              </div>
              <label className="flex items-center justify-between text-xs">
                <div><div className="text-slate-200">Functional</div><div className="text-slate-500">Remembers your workspace and layout</div></div>
                <input type="checkbox" checked={preferences.functional} onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })} className="accent-cyan-400" data-testid="cookie-functional" />
              </label>
              <label className="flex items-center justify-between text-xs">
                <div><div className="text-slate-200">Analytics</div><div className="text-slate-500">Anonymous usage — not enabled yet</div></div>
                <input type="checkbox" checked={preferences.analytics} onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })} className="accent-cyan-400" data-testid="cookie-analytics" />
              </label>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => save({ functional: true, analytics: true })} className="fx-btn-primary text-white text-xs font-medium px-3 py-1.5 rounded" data-testid="cookie-accept-all">Accept all</button>
            <button onClick={() => save({ functional: false, analytics: false })} className="border border-white/10 hover:border-white/20 text-slate-200 text-xs px-3 py-1.5 rounded" data-testid="cookie-reject-all">Reject all</button>
            {expand ? (
              <button onClick={() => save(preferences)} className="border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-xs px-3 py-1.5 rounded" data-testid="cookie-save">Save choices</button>
            ) : (
              <button onClick={() => setExpand(true)} className="text-cyan-300 hover:text-cyan-200 text-xs px-1 py-1.5" data-testid="cookie-preferences-btn">Preferences</button>
            )}
          </div>
        </div>
        <button onClick={() => save({ functional: false, analytics: false })} className="text-slate-500 hover:text-white p-1" aria-label="Close" data-testid="cookie-close"><X size={14} /></button>
      </div>
    </div>
  );
}
